import { Component, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { hasError: boolean };

// Lightweight shell error boundary. It replaces Sentry.ErrorBoundary in the
// first-paint critical path so the full Sentry SDK (including the ~258 kB
// Session Replay integration) can be loaded lazily after mount instead of being
// pulled into the entry chunk. Errors are still forwarded to Sentry once it has
// initialized — captureException is a safe no-op if it has not yet loaded.
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    void import("@sentry/react")
      .then((Sentry) => {
        if (Sentry.getClient()) {
          Sentry.captureException(error, { extra: { componentStack: info } });
        }
      })
      .catch(() => {
        /* Sentry unavailable — nothing else to do */
      });
  }

  render() {
    if (this.state.hasError) return <p>Something went wrong</p>;
    return this.props.children;
  }
}

export default RootErrorBoundary;
